import { useState, useEffect } from 'react';
import { useCreateOAuthConfig, useOAuthRedirectUri, useTestOAuthConnection } from '../../hooks/useOAuthConfigs';
import { ProviderIcon } from './ProviderIcon';
import type { OAuthProvider, CreateOAuthConfigRequest } from '../../types/oauth';
import { PROVIDER_DISPLAY_NAMES, PROVIDER_DOCS, DEFAULT_SCOPES } from '../../types/oauth';
import './OAuthSetupWizard.css';

interface OAuthSetupWizardProps {
  projectId: string;
  provider: OAuthProvider;
  onClose: () => void;
  onSuccess: () => void;
}

type WizardStep = 'intro' | 'credentials' | 'scopes' | 'settings' | 'test' | 'complete';

export function OAuthSetupWizard({ projectId, provider, onClose, onSuccess }: OAuthSetupWizardProps) {
  const [step, setStep] = useState<WizardStep>('intro');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [scopes, setScopes] = useState<string[]>(DEFAULT_SCOPES[provider] || []);
  const [customScope, setCustomScope] = useState('');
  const [providerSettings, setProviderSettings] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: redirectUriData } = useOAuthRedirectUri(projectId, provider);
  const createConfig = useCreateOAuthConfig();
  const testConnection = useTestOAuthConnection();

  const redirectUri = redirectUriData?.redirect_uri || '';

  // Initialize provider-specific settings
  useEffect(() => {
    if (provider === 'okta') {
      setProviderSettings({ issuer: '' });
    } else if (provider === 'microsoft') {
      setProviderSettings({ tenant_id: 'common' });
    } else if (provider === 'google') {
      setProviderSettings({ hosted_domain: '' });
    } else if (provider === 'github') {
      setProviderSettings({});
    }
  }, [provider]);

  const handleCopyRedirectUri = async () => {
    await navigator.clipboard.writeText(redirectUri);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddScope = () => {
    const trimmed = customScope.trim();
    if (trimmed && !scopes.includes(trimmed)) {
      setScopes([...scopes, trimmed]);
      setCustomScope('');
    }
  };

  const handleRemoveScope = (scope: string) => {
    setScopes(scopes.filter((s) => s !== scope));
  };

  const handleCreateConfig = async () => {
    const request: CreateOAuthConfigRequest = {
      provider,
      client_id: clientId.trim(),
      client_secret: clientSecret,
      scopes,
      provider_settings: Object.fromEntries(
        Object.entries(providerSettings).filter(([, v]) => v !== '')
      ),
    };

    try {
      await createConfig.mutateAsync({ projectId, data: request });
      setStep('test');
    } catch {
      // Error handled by mutation
    }
  };

  const handleTestConnection = async () => {
    setTestResult(null);
    try {
      await testConnection.mutateAsync({ projectId, provider });
      setTestResult({ success: true, message: 'Connection successful! Your OAuth configuration is working.' });
      setStep('complete');
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Connection test failed. Please check your credentials.',
      });
    }
  };

  const handleSkipTest = () => {
    setStep('complete');
  };

  const canProceedFromCredentials = clientId.trim() && clientSecret.trim();
  const canProceedFromSettings = provider !== 'okta' || providerSettings.issuer?.trim();

  const renderStepIndicator = () => {
    const steps: { key: WizardStep; label: string }[] = [
      { key: 'intro', label: 'Introduction' },
      { key: 'credentials', label: 'Credentials' },
      { key: 'scopes', label: 'Scopes' },
      { key: 'settings', label: 'Settings' },
      { key: 'test', label: 'Test' },
    ];

    const currentIndex = steps.findIndex((s) => s.key === step);

    return (
      <div className="wizard-steps">
        {steps.map((s, i) => (
          <div
            key={s.key}
            className={`wizard-step ${i <= currentIndex ? 'wizard-step--active' : ''} ${
              i < currentIndex ? 'wizard-step--completed' : ''
            }`}
          >
            <span className="wizard-step__number">{i + 1}</span>
            <span className="wizard-step__label">{s.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderIntroStep = () => (
    <div className="wizard-content">
      <div className="wizard-intro">
        <div className="wizard-intro__header">
          <div className={`provider-icon-box provider-icon-box--lg provider-icon-box--${provider}`}>
            <ProviderIcon provider={provider} size="lg" />
          </div>
          <h3>Configure {PROVIDER_DISPLAY_NAMES[provider]}</h3>
        </div>
        <p>
          Follow these steps to set up {PROVIDER_DISPLAY_NAMES[provider]} OAuth for your project.
          You'll need to create an OAuth application in the {PROVIDER_DISPLAY_NAMES[provider]} developer console.
        </p>

        <div className="wizard-intro__redirect">
          <h4>1. Copy your Redirect URI</h4>
          <p>Add this URI to your OAuth application's allowed redirect URIs:</p>
          <div className="redirect-uri-box">
            <code>{redirectUri || 'Loading...'}</code>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={handleCopyRedirectUri}
              disabled={!redirectUri}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="wizard-intro__docs">
          <h4>2. Create an OAuth Application</h4>
          <p>
            Visit the {PROVIDER_DISPLAY_NAMES[provider]} developer console to create a new OAuth application:
          </p>
          <a
            href={PROVIDER_DOCS[provider]}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--secondary"
          >
            Open {PROVIDER_DISPLAY_NAMES[provider]} Console
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="icon-external">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );

  const renderCredentialsStep = () => (
    <div className="wizard-content">
      <h3>Enter Credentials</h3>
      <p>Enter the Client ID and Client Secret from your {PROVIDER_DISPLAY_NAMES[provider]} OAuth application.</p>

      <div className="form-group">
        <label htmlFor="clientId">Client ID</label>
        <input
          id="clientId"
          type="text"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder={getClientIdPlaceholder(provider)}
          autoComplete="off"
        />
        <span className="form-hint">{getClientIdHint(provider)}</span>
      </div>

      <div className="form-group">
        <label htmlFor="clientSecret">Client Secret</label>
        <input
          id="clientSecret"
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          placeholder="Enter your client secret"
          autoComplete="new-password"
        />
        <span className="form-hint">This will be encrypted and stored securely.</span>
      </div>
    </div>
  );

  const renderScopesStep = () => (
    <div className="wizard-content">
      <h3>Configure Scopes</h3>
      <p>
        Scopes determine what data your application can access. The default scopes are recommended
        for basic user authentication.
      </p>

      <div className="scopes-list">
        {scopes.map((scope) => (
          <div key={scope} className="scope-tag">
            <span>{scope}</span>
            <button
              type="button"
              className="scope-tag__remove"
              onClick={() => handleRemoveScope(scope)}
              aria-label={`Remove ${scope}`}
            >
              &times;
            </button>
          </div>
        ))}
      </div>

      <div className="add-scope">
        <input
          type="text"
          value={customScope}
          onChange={(e) => setCustomScope(e.target.value)}
          placeholder="Add custom scope"
          onKeyDown={(e) => e.key === 'Enter' && handleAddScope()}
        />
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={handleAddScope}
          disabled={!customScope.trim()}
        >
          Add
        </button>
      </div>

      <button
        type="button"
        className="btn btn--link"
        onClick={() => setScopes(DEFAULT_SCOPES[provider] || [])}
      >
        Reset to defaults
      </button>
    </div>
  );

  const renderSettingsStep = () => (
    <div className="wizard-content">
      <h3>Provider Settings</h3>
      {renderProviderSettings()}
    </div>
  );

  const renderProviderSettings = () => {
    switch (provider) {
      case 'google':
        return (
          <>
            <p>Configure additional settings for Google OAuth.</p>
            <div className="form-group">
              <label htmlFor="hostedDomain">Hosted Domain (Optional)</label>
              <input
                id="hostedDomain"
                type="text"
                value={providerSettings.hosted_domain || ''}
                onChange={(e) => setProviderSettings({ ...providerSettings, hosted_domain: e.target.value })}
                placeholder="example.com"
              />
              <span className="form-hint">
                Restrict sign-in to users from a specific Google Workspace domain.
              </span>
            </div>
          </>
        );

      case 'microsoft':
        return (
          <>
            <p>Configure your Azure AD tenant settings.</p>
            <div className="form-group">
              <label htmlFor="tenantId">Tenant ID</label>
              <select
                id="tenantId"
                value={providerSettings.tenant_id || 'common'}
                onChange={(e) => setProviderSettings({ ...providerSettings, tenant_id: e.target.value })}
              >
                <option value="common">Common (Any Microsoft account)</option>
                <option value="organizations">Organizations only (Work/School accounts)</option>
                <option value="consumers">Consumers only (Personal accounts)</option>
                <option value="custom">Specific tenant...</option>
              </select>
              {providerSettings.tenant_id === 'custom' && (
                <input
                  type="text"
                  value={providerSettings.custom_tenant || ''}
                  onChange={(e) => setProviderSettings({ ...providerSettings, tenant_id: e.target.value })}
                  placeholder="Enter tenant UUID"
                  className="mt-2"
                />
              )}
              <span className="form-hint">
                Controls which Microsoft accounts can sign in to your application.
              </span>
            </div>
          </>
        );

      case 'okta':
        return (
          <>
            <p>Configure your Okta organization settings.</p>
            <div className="form-group">
              <label htmlFor="issuer">Okta Issuer URL *</label>
              <input
                id="issuer"
                type="url"
                value={providerSettings.issuer || ''}
                onChange={(e) => setProviderSettings({ ...providerSettings, issuer: e.target.value })}
                placeholder="https://dev-123456.okta.com"
                required
              />
              <span className="form-hint">
                Your Okta organization URL (e.g., https://your-org.okta.com).
              </span>
            </div>
            <div className="form-group">
              <label htmlFor="authServer">Authorization Server (Optional)</label>
              <input
                id="authServer"
                type="text"
                value={providerSettings.authorization_server || ''}
                onChange={(e) => setProviderSettings({ ...providerSettings, authorization_server: e.target.value })}
                placeholder="default"
              />
              <span className="form-hint">
                Custom authorization server ID. Leave empty to use the default.
              </span>
            </div>
          </>
        );

      case 'github':
        return (
          <>
            <p>GitHub OAuth uses standard settings. No additional configuration required.</p>
            <div className="wizard-info-box">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              </svg>
              <span>You can proceed to test your configuration.</span>
            </div>
          </>
        );

      default:
        return <p>No additional settings required for this provider.</p>;
    }
  };

  const renderTestStep = () => (
    <div className="wizard-content">
      <h3>Test Connection</h3>
      <p>
        Your OAuth configuration has been saved. Test the connection to verify your credentials
        are working correctly.
      </p>

      {testResult && (
        <div className={`wizard-test-result wizard-test-result--${testResult.success ? 'success' : 'error'}`}>
          <span className="wizard-test-result__icon">
            {testResult.success ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            )}
          </span>
          <span>{testResult.message}</span>
        </div>
      )}

      <div className="wizard-test-actions">
        <button
          type="button"
          className="btn btn--primary"
          onClick={handleTestConnection}
          disabled={testConnection.isPending}
        >
          {testConnection.isPending ? 'Testing...' : 'Test Connection'}
        </button>
        <button
          type="button"
          className="btn btn--link"
          onClick={handleSkipTest}
        >
          Skip for now
        </button>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="wizard-content wizard-content--complete">
      <div className="wizard-complete">
        <div className="wizard-complete__icons">
          <div className={`provider-icon-box provider-icon-box--lg provider-icon-box--${provider}`}>
            <ProviderIcon provider={provider} size="lg" />
          </div>
          <div className="wizard-complete__checkmark">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <h3>{PROVIDER_DISPLAY_NAMES[provider]} Configured Successfully!</h3>
        <p>
          Your OAuth provider is now configured. You can enable it from the provider list
          to start accepting sign-ins.
        </p>
        <button
          type="button"
          className="btn btn--primary"
          onClick={onSuccess}
        >
          Done
        </button>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 'intro':
        return renderIntroStep();
      case 'credentials':
        return renderCredentialsStep();
      case 'scopes':
        return renderScopesStep();
      case 'settings':
        return renderSettingsStep();
      case 'test':
        return renderTestStep();
      case 'complete':
        return renderCompleteStep();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (step === 'complete') return null;

    return (
      <footer className="wizard-footer">
        {step !== 'intro' && step !== 'test' && (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => {
              const steps: WizardStep[] = ['intro', 'credentials', 'scopes', 'settings', 'test'];
              const currentIndex = steps.indexOf(step);
              if (currentIndex > 0) setStep(steps[currentIndex - 1]);
            }}
          >
            Back
          </button>
        )}
        <div className="wizard-footer__spacer" />
        {step === 'intro' && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setStep('credentials')}
          >
            Get Started
          </button>
        )}
        {step === 'credentials' && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setStep('scopes')}
            disabled={!canProceedFromCredentials}
          >
            Continue
          </button>
        )}
        {step === 'scopes' && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setStep('settings')}
          >
            Continue
          </button>
        )}
        {step === 'settings' && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleCreateConfig}
            disabled={!canProceedFromSettings || createConfig.isPending}
          >
            {createConfig.isPending ? 'Saving...' : 'Save Configuration'}
          </button>
        )}
      </footer>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--wizard" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Set Up {PROVIDER_DISPLAY_NAMES[provider]}</h2>
          <button className="modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </header>

        {step !== 'complete' && renderStepIndicator()}

        <div className="wizard-body">
          {createConfig.isError && (
            <div className="wizard-error">
              <p>{createConfig.error?.message || 'Failed to create configuration'}</p>
            </div>
          )}
          {renderCurrentStep()}
        </div>

        {renderFooter()}
      </div>
    </div>
  );
}

function getClientIdPlaceholder(provider: OAuthProvider): string {
  switch (provider) {
    case 'google':
      return '123456789-abc.apps.googleusercontent.com';
    case 'github':
      return 'Iv1.abc123def456';
    case 'microsoft':
      return '12345678-1234-1234-1234-123456789012';
    case 'okta':
      return '0oa1abc2def3ghi4j5k6';
    default:
      return 'Enter your client ID';
  }
}

function getClientIdHint(provider: OAuthProvider): string {
  switch (provider) {
    case 'google':
      return 'Found in Google Cloud Console under APIs & Services > Credentials';
    case 'github':
      return 'Found in GitHub Settings > Developer settings > OAuth Apps';
    case 'microsoft':
      return 'Found in Azure Portal under App registrations > Application (client) ID';
    case 'okta':
      return 'Found in Okta Admin Console under Applications > Your App > General';
    default:
      return '';
  }
}
